import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { EVENEMENT_SESSION_EXPIREE, ErreurApi, appelApi, urlApi } from './api';
import { parametresVueGlobale } from './useVueGlobale';
import type { TypeFacturation } from './useFinancements';

/**
 * Pilotage des financeurs et OPCO (phase 5, TF-428) : onglet « Pilotage par
 * financeur » de « Financeurs & OPCO ». Une ligne par financeur — dossiers,
 * références DK, pris en charge, facturé, encaissé, reste dû, retards — et la
 * liste des dossiers retenus. Tout vient de l'API
 * (`domain/pilotage-financeurs.ts`) : l'écran ne recalcule rien.
 */

export type StatutDossierFinance = 'EN_COURS' | 'A_VENIR' | 'TERMINE' | 'ROMPU' | 'DATES_A_PRECISER';
export type FiltreRetard = 'PAIEMENT' | 'FACTURATION' | 'AUCUN';
export type TypeClient = 'OPCO' | 'FRANCE_TRAVAIL' | 'AUTRE' | 'ENTREPRISE';

export interface MontantPilote {
  centimes: number;
  texte: string;
}

export interface RetardPaiement {
  nombre: number;
  montant: MontantPilote;
  /** Jours écoulés depuis l'échéance de paiement la plus ancienne ; `null` sans retard. */
  joursMax: number | null;
}

export interface SyntheseFinanceur {
  /** Clé du client (`financeur:<id>`, `entreprise:<id>`), `SANS_PAYEUR` ou `TOTAL`. */
  cle: string;
  nom: string;
  type: TypeClient | null;
  typeLibelle: string | null;
  dossiers: { total: number; parStatut: Record<StatutDossierFinance, number> };
  references: { saisies: number; manquantes: number };
  engage: MontantPilote | null;
  montantsInconnus: number;
  /** Coût pédagogique au-delà des ruptures, qui ne se facturera pas. */
  nonFacturable: MontantPilote | null;
  facture: MontantPilote;
  encaisse: MontantPilote;
  resteDu: MontantPilote;
  enRetard: RetardPaiement;
  delaiMoyenReglement: number | null;
  facturesReglees: number;
  resteAFacturer: MontantPilote | null;
  depassement: MontantPilote | null;
  aFacturer: number;
}

export interface DossierPilote {
  cle: string;
  nature: 'APPRENTISSAGE' | 'ACCORD_OF';
  typeFacturation: TypeFacturation | null;
  typeLibelle: string | null;
  apprenant: { id: string; nom: string; prenom: string };
  formation: string;
  siteCode: string;
  employeur: string | null;
  dateDebut: string | null;
  dateFin: string | null;
  statut: { etat: StatutDossierFinance; libelle: string; ton: string };
  client: { cle: string; nom: string; type: TypeClient } | null;
  /** Faux pour un ancien payeur, dont il reste des factures. */
  payeurActuel: boolean;
  reference: string | null;
  referenceManquante: boolean;
  engage: MontantPilote | null;
  /** Coût pédagogique au-delà de la rupture, qui ne se facturera pas. */
  nonFacturable: MontantPilote | null;
  facture: MontantPilote;
  encaisse: MontantPilote;
  resteDu: MontantPilote;
  enRetard: RetardPaiement;
  delaiMoyenReglement: number | null;
  resteAFacturer: MontantPilote | null;
  depassement: MontantPilote | null;
  aFacturer: { nombre: number; texte: string | null };
}

export interface PilotageFinanceurs {
  financeurs: SyntheseFinanceur[];
  total: SyntheseFinanceur;
  dossiers: DossierPilote[];
  perimetre: { dossiers: number; parcoursSansAccord: number };
  filtres: {
    financeurs: { valeur: string; libelle: string; typeLibelle: string | null }[];
    types: { valeur: string; libelle: string }[];
    /** Apprentissage, contrat de professionnalisation, France Travail (TF-465). */
    typesFacturation: { valeur: TypeFacturation; libelle: string }[];
    statuts: { valeur: StatutDossierFinance; libelle: string }[];
    retards: { valeur: FiltreRetard; libelle: string }[];
  };
  calculeLe: string;
}

export interface FiltresPilotage {
  site: string;
  financeur: string;
  type: string;
  typeFacturation: string;
  statut: string;
  retard: string;
  recherche: string;
}

export function usePilotageFinanceurs(filtres: FiltresPilotage) {
  const chaine = parametresVueGlobale({ ...filtres });
  return useQuery({
    queryKey: ['financements', 'pilotage', chaine],
    queryFn: () => appelApi<PilotageFinanceurs>(`/financements/pilotage?${chaine}`),
    placeholderData: keepPreviousData,
  });
}

/**
 * Télécharge le classeur de la vue filtrée — « Par financeur » et « Dossiers ».
 * Par `fetch` plutôt que par un lien : un refus (rien à exporter) s'affiche à
 * l'écran au lieu de s'enregistrer comme un faux fichier. Rend le nombre de
 * dossiers exportés.
 */
export async function exporterPilotage(filtres: FiltresPilotage): Promise<{ nomFichier: string; lignes: number }> {
  const reponse = await fetch(urlApi(`/financements/pilotage/export?${parametresVueGlobale({ ...filtres })}`), {
    method: 'POST',
    credentials: 'include',
  });
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as
      | { erreur?: { code?: string; message?: string; details?: unknown } }
      | null;
    if (reponse.status === 401) window.dispatchEvent(new Event(EVENEMENT_SESSION_EXPIREE));
    throw new ErreurApi(
      reponse.status,
      erreur?.erreur?.code ?? 'ERREUR',
      erreur?.erreur?.message ?? 'L’export n’a pas pu être produit.',
      erreur?.erreur?.details,
    );
  }
  const nomFichier =
    /filename="([^"]+)"/.exec(reponse.headers.get('content-disposition') ?? '')?.[1] ?? 'pilotage-financeurs.xlsx';
  const url = URL.createObjectURL(await reponse.blob());
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  document.body.removeChild(lien);
  URL.revokeObjectURL(url);
  return { nomFichier, lignes: Number(reponse.headers.get('x-lignes') ?? 0) };
}
